import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  IconButton, 
  Button, 
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '../store/authStore';

const SessionsPage = () => {
  const navigate = useNavigate();
  const { getSessions, invalidateSession, invalidateAllSessions } = useAuthStore();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [openAllDialog, setOpenAllDialog] = useState(false);
  
  const handleBackToSettings = () => {
    navigate('/settings');
  };

  // Fetch sessions on component mount
  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const data = await getSessions();
      setSessions(data);
      setError(null);
    } catch (err) {
      setError('Не удалось загрузить сессии');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvalidateSession = async () => {
    if (!selectedSession) return;
    
    try {
      const success = await invalidateSession(selectedSession.id);
      if (success) {
        setSessions(sessions.filter(session => session.id !== selectedSession.id));
        setOpenDialog(false);
      }
    } catch (err) {
      setError('Не удалось завершить сессию');
      console.error(err);
    }
  };

  const handleInvalidateAllSessions = async () => {
    try {
      const success = await invalidateAllSessions();
      if (success) {
        // Refresh sessions list
        fetchSessions();
        setOpenAllDialog(false);
      }
    } catch (err) {
      setError('Не удалось завершить все сессии');
      console.error(err);
    }
  };

  const openConfirmDialog = (session) => {
    setSelectedSession(session);
    setOpenDialog(true);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: ru });
    } catch (e) {
      return 'Неизвестно';
    }
  };

  const getBrowserInfo = (userAgent) => {
    if (!userAgent) return 'Неизвестное устройство';
    
    // Simple browser detection
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident')) return 'Internet Explorer';
    
    return 'Браузер';
  };

  const getDeviceInfo = (userAgent) => {
    if (!userAgent) return '';
    
    // Simple device detection
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Macintosh')) return 'Mac';
    if (userAgent.includes('Linux')) return 'Linux';
    
    return '';
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToSettings}
        sx={{ mb: 2 }}
      >
        Вернуться в настройки
      </Button>
      
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" component="h1">
            Управление сессиями
          </Typography>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => setOpenAllDialog(true)}
            disabled={sessions.length <= 1}
          >
            Завершить все другие сессии
          </Button>
        </Box>
        
        <Typography variant="body1" paragraph>
          Здесь вы можете просмотреть все ваши активные сессии и при необходимости завершить их.
          Сессии автоматически истекают через 7 дней после последней активности.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : sessions.length === 0 ? (
          <Typography variant="body1" color="textSecondary" align="center" py={4}>
            У вас нет активных сессий
          </Typography>
        ) : (
          <List>
            {sessions.map((session, index) => (
              <React.Fragment key={session.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <DevicesIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">
                          {getBrowserInfo(session.userAgent)} на {getDeviceInfo(session.userAgent) || 'устройстве'}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          IP: {session.ipAddress || 'Неизвестно'}
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Последняя активность: {formatDate(session.lastActivity)}
                        </Typography>
                        <Typography variant="body2" component="span" display="block">
                          Истекает: {formatDate(session.expiresAt)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={() => openConfirmDialog(session)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Dialog for single session invalidation */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>Подтверждение</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите завершить эту сессию? 
            Если это ваша текущая сессия, вам придется войти снова.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Отмена
          </Button>
          <Button onClick={handleInvalidateSession} color="error">
            Завершить сессию
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for all sessions invalidation */}
      <Dialog
        open={openAllDialog}
        onClose={() => setOpenAllDialog(false)}
      >
        <DialogTitle>Подтверждение</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите завершить все другие сессии? 
            Это выйдет из всех других устройств, где вы вошли в систему.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAllDialog(false)} color="primary">
            Отмена
          </Button>
          <Button onClick={handleInvalidateAllSessions} color="error">
            Завершить все сессии
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SessionsPage;
